import { ERROR_CODE_MESSAGES } from './error-codes';
import type { ClassifiedSaveError } from './steps.types';

/**
 * Returns true when a save/submit error is a `STEP_LOCKED` rejection from the
 * server — the portal must refresh the step-access snapshot and re-render the
 * locked state instead of showing a generic error toast.
 *
 * Real rejections arrive as `lib/api.ts`'s `ApiError`, which carries the
 * machine-readable code in `.code` — the human `.message` is a plain sentence
 * that NEVER contains the literal token "STEP_LOCKED" (that mismatch was the
 * root cause of this bug: this function used to check `.message` only, so it
 * always returned false for a real 423 rejection). Check `.code` first for
 * any ApiError-shaped input; fall back to the message substring check only
 * for inputs that aren't ApiError-shaped (e.g. a plain Error/string a caller
 * constructed itself), preserving prior behaviour for those.
 */
export function isStepLockedError(e: unknown): boolean {
  if (!e) return false;
  if (typeof e === 'object' && 'code' in (e as Record<string, unknown>)) {
    return (e as { code?: unknown }).code === 'STEP_LOCKED';
  }
  const msg = (e instanceof Error ? e.message : String(e)).toUpperCase();
  return msg.includes('STEP_LOCKED');
}

/**
 * Classifies a step save/submit rejection into an applicant-safe message,
 * branching on the server's structured `ApiError.code`/`.status` instead of
 * collapsing every non-network failure into one misleading "check your
 * connection" banner (Asana 1217767958880539). Shared by ApplicationPage's
 * three save call sites (the form engine's onError, the showcase manual
 * save, and the debounced autosave status line) so they classify identically
 * instead of drifting via three separate regex/allowlist copies.
 *
 * A true network/timeout failure — fetch never got a response, so there is
 * no `.status` at all — is the ONLY case that keeps the "check your
 * connection" framing; it is the only case where that message is honest.
 * Every other branch reflects a real, structured rejection the server sent.
 */
export function classifySaveError(e: unknown): ClassifiedSaveError {
  const err = e as { code?: unknown; status?: unknown } | null | undefined;
  const code = typeof err?.code === 'string' ? err.code : undefined;
  const status = typeof err?.status === 'number' ? err.status : undefined;
  const message = e instanceof Error ? e.message : undefined;

  if (code === 'STEP_LOCKED') {
    return { code: 'step_locked', message: ERROR_CODE_MESSAGES.STEP_LOCKED };
  }
  if (code === 'PASSPORT_PROVENANCE_UNAVAILABLE') {
    return {
      code: 'server_error',
      message: ERROR_CODE_MESSAGES.PASSPORT_PROVENANCE_UNAVAILABLE,
    };
  }
  // Profile Picture past the 5MB PRD limit (413 from the BFF). Prefer the
  // structured status; keep the message-regex fallback for any path that
  // still surfaces this as a plain Error without a status (defensive).
  if (status === 413 || /413|too.?large|5MB/i.test(message ?? '')) {
    return {
      code: 'other',
      message: 'Your profile photo is too large — please choose an image under 5MB and save again.',
    };
  }
  if (code === 'AGREEMENT_VERSION_STALE' || code === 'AGREEMENT_VIEW_STALE') {
    return { code: 'agreement_version_stale', message: ERROR_CODE_MESSAGES.AGREEMENT_VERSION_STALE };
  }
  if (code === 'AGREEMENT_NOT_APPLICABLE') {
    return { code: 'agreement_not_applicable', message: ERROR_CODE_MESSAGES.AGREEMENT_NOT_APPLICABLE };
  }
  if (code === 'TYPED_NAME_REQUIRED') {
    return { code: 'typed_name_required', message: ERROR_CODE_MESSAGES.TYPED_NAME_REQUIRED };
  }
  if (status === 429) {
    return { code: 'rate_limited', message: "You're saving a bit fast — please wait a moment and try again." };
  }
  if (typeof status === 'number' && status >= 500) {
    return {
      code: 'server_error',
      message: "Something went wrong on our end. Your entries are still on this page — please try again shortly.",
    };
  }
  if (typeof status === 'number') {
    // Any other structured rejection the BFF/Globus sent (e.g. the SWT
    // backwards-vacation-dates 422 from enforceSwtVacationWindow) — the
    // message is applicant-safe per AP-1 (Globus/BFF error messages render
    // verbatim), so show it instead of a generic banner that would hide real,
    // actionable information.
    return {
      code: 'other',
      message: message || "We couldn't save your answers. Please review and try again.",
    };
  }
  // No `.status` at all: fetch never got a response — a genuine network/
  // timeout failure. The only branch where "check your connection" is honest.
  return {
    code: 'network',
    message: "We couldn't save your answers. Your entries are still on this page — please check your connection and try again.",
  };
}
